const SIZES = {
	xs: 16,
	sm: 32,
	md: 40,
	lg: 48,
} as const;

type VideoIconVariant =
	| "play"
	| "bell"
	| "clock"
	| "scissors"
	| "clipboard"
	| "target";

interface VideoIconProps {
	variant: VideoIconVariant;
	size?: keyof typeof SIZES;
	color?: string;
	className?: string;
}

function IconSvg({
	variant,
	glyphSize,
	hasBackground,
	iconColor,
}: {
	variant: VideoIconVariant;
	glyphSize: number;
	hasBackground: boolean;
	iconColor: string;
}) {
	const primary = hasBackground ? "white" : iconColor;
	const accent = hasBackground ? "#FF0000" : iconColor;
	const s = { fill: primary, stroke: primary, strokeWidth: 0 };

	switch (variant) {
		case "play":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<polygon points="8,4 20,12 8,20" {...s} />
				</svg>
			);
		case "bell":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<path
						d="M12 2C8 2 5 5 5 9L5 14L3 18L21 18L19 14L19 9C19 5 16 2 12 2Z"
						{...s}
					/>
					<path
						d="M9 18C9 20.5 10.5 22 12 22C13.5 22 15 20.5 15 18"
						fill="none"
						stroke={primary}
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</svg>
			);
		case "clock":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<circle
						cx="12"
						cy="12"
						r="10"
						fill={hasBackground ? "white" : "none"}
						stroke={hasBackground ? "none" : primary}
						strokeWidth={hasBackground ? 0 : 2}
					/>
					<line
						x1="12"
						y1="6"
						x2="12"
						y2="12"
						stroke={accent}
						strokeWidth="2.5"
						strokeLinecap="round"
					/>
					<line
						x1="12"
						y1="12"
						x2="16"
						y2="14"
						stroke={accent}
						strokeWidth="2.5"
						strokeLinecap="round"
					/>
				</svg>
			);
		case "scissors":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<circle
						cx="6"
						cy="6"
						r="3"
						stroke={primary}
						strokeWidth="2"
						fill="none"
					/>
					<circle
						cx="6"
						cy="18"
						r="3"
						stroke={primary}
						strokeWidth="2"
						fill="none"
					/>
					<line
						x1="20"
						y1="4"
						x2="8.5"
						y2="15.5"
						stroke={primary}
						strokeWidth="2"
						strokeLinecap="round"
					/>
					<line
						x1="14"
						y1="10"
						x2="20"
						y2="20"
						stroke={primary}
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</svg>
			);
		case "clipboard":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<rect
						x="4"
						y="4"
						width="16"
						height="18"
						rx="2"
						stroke={primary}
						strokeWidth="2"
						fill="none"
					/>
					<rect x="8" y="2" width="8" height="4" rx="1" fill={primary} />
					<line
						x1="8"
						y1="11"
						x2="16"
						y2="11"
						stroke={primary}
						strokeWidth="2"
						strokeLinecap="round"
					/>
					<line
						x1="8"
						y1="15"
						x2="13"
						y2="15"
						stroke={primary}
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</svg>
			);
		case "target":
			return (
				<svg
					aria-hidden="true"
					width={glyphSize}
					height={glyphSize}
					viewBox="0 0 24 24"
					fill="none"
				>
					<circle
						cx="12"
						cy="12"
						r="10"
						stroke={primary}
						strokeWidth="2"
						fill="none"
					/>
					<circle
						cx="12"
						cy="12"
						r="6"
						stroke={primary}
						strokeWidth="2"
						fill="none"
					/>
					<circle cx="12" cy="12" r="2" fill={primary} />
				</svg>
			);
	}
}

export function VideoIcon({
	variant,
	size = "md",
	color = "#FF0000",
	className,
}: VideoIconProps) {
	const px = SIZES[size];
	const hasBackground = size !== "xs";
	const glyphSize = size === "xs" ? 16 : 18;
	const iconColor = color;
	return (
		<span
			className={className}
			style={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: px,
				height: px,
				flexShrink: 0,
				color: iconColor,
				...(hasBackground
					? {
							borderRadius: px / 2,
							background: "#FF0000",
						}
					: {}),
			}}
		>
			<IconSvg
				variant={variant}
				glyphSize={glyphSize}
				hasBackground={hasBackground}
				iconColor={iconColor}
			/>
		</span>
	);
}
